// src/pages/documentation/quick-start/QuickStartOnboarding.jsx

import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faStore,
    faTags,
    faUsers,
    faBullseye,
    faChartBar,
    faQrcode,
    faCheckCircle,
    faArrowRight,
    faArrowLeft,
    faInfoCircle,
    faClock,
    faRocket
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

const QuickStartOnboarding = () => {
    const onboardingSteps = [
        {
            step: 1,
            title: 'Crea tu Marca',
            icon: faTags,
            color: 'blue',
            description: 'Define el nombre y la identidad de tu marca o negocio',
            details: 'Tu marca es cómo te conocerán tus clientes. Puede ser el nombre de tu tienda, restaurante o negocio.',
            tips: [
                'Usa un nombre claro y fácil de recordar',
                'Puedes agregar un logo más adelante',
                'Puedes tener varias marcas si tienes múltiples negocios'
            ],
            whatHappens: 'Después de crear tu marca, podrás personalizarla con colores, logo y otros detalles.'
        },
        {
            step: 2,
            title: 'Conoce a tu Público',
            icon: faUsers,
            color: 'green',
            description: 'Responde algunas preguntas sobre tus clientes ideales',
            details: 'Esto nos ayuda a entender mejor a quién le vendes y crear campañas más efectivas para tu negocio.',
            tips: [
                'Responde con honestidad, no hay respuestas correctas o incorrectas',
                'Piensa en tu cliente más frecuente',
                'Puedes cambiar esta información después si es necesario'
            ],
            whatHappens: 'Con esta información, PetitPrime podrá sugerirte estrategias de marketing personalizadas.'
        },
        {
            step: 3,
            title: 'Configura tu Tienda',
            icon: faStore,
            color: 'purple',
            description: 'Agrega la información de tu local o tienda física',
            details: 'Incluye la dirección, horarios y cualquier información relevante que quieras compartir con tus clientes.',
            tips: [
                'La dirección ayuda a los clientes a encontrarte',
                'Puedes agregar múltiples tiendas si tienes varias ubicaciones',
                'Puedes editar esta información en cualquier momento'
            ],
            whatHappens: 'Esta información aparecerá en la tarjeta digital que recibirán tus clientes.'
        },
        {
            step: 4,
            title: 'Define tu Meta',
            icon: faBullseye,
            color: 'red',
            description: 'Selecciona qué objetivo quieres lograr con PetitPrime',
            details: 'Puedes elegir entre aumentar ventas, fidelizar clientes, atraer nuevos clientes, etc. Esto ayuda a personalizar tu experiencia.',
            tips: [
                'Puedes cambiar tu objetivo después',
                'Puedes tener múltiples objetivos activos',
                'El sistema te sugerirá acciones según tu objetivo'
            ],
            whatHappens: 'PetitPrime adaptará sus recomendaciones y estrategias según el objetivo que elijas.'
        },
        {
            step: 5,
            title: 'Genera tu QR',
            icon: faQrcode,
            color: 'indigo',
            description: 'Crea el código QR que tus clientes escanearán para registrarse',
            details: 'Este QR es único para tu negocio. Puedes imprimirlo, mostrarlo en pantalla o compartirlo digitalmente.',
            tips: [
                'Coloca el QR en un lugar visible de tu tienda',
                'Puedes generar múltiples QRs para diferentes ubicaciones',
                'Los clientes escanean el QR con su teléfono y se registran automáticamente'
            ],
            whatHappens: 'Una vez generado el QR, podrás descargarlo en PDF e imprimirlo para colocarlo en tu tienda.'
        }
    ];

    // Mapeo de colores para clases de Tailwind
    const colorClasses = {
        blue: {
            bg: 'bg-blue-100',
            text: 'text-blue-600',
            border: 'border-blue-200'
        },
        green: {
            bg: 'bg-green-100',
            text: 'text-green-600',
            border: 'border-green-200'
        },
        purple: {
            bg: 'bg-purple-100',
            text: 'text-purple-600',
            border: 'border-purple-200'
        },
        red: {
            bg: 'bg-red-100',
            text: 'text-red-600',
            border: 'border-red-200'
        },
        indigo: {
            bg: 'bg-indigo-100',
            text: 'text-indigo-600',
            border: 'border-indigo-200'
        }
    };

    return (
        <DocumentationLayout>
            <div className="p-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-purple-600 rounded-lg">
                            <FontAwesomeIcon icon={faRocket} className="text-white text-2xl" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Paso 3: Configurar tu negocio</h1>
                            <p className="text-gray-600">Completa el proceso de onboarding paso a paso</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Link to="/documentation" className="hover:text-gray-700">Centro de Ayuda</Link>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        <Link to="/documentation/quick-start" className="hover:text-gray-700">Inicio Rápido</Link>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        <span className="text-gray-700">Onboarding</span>
                    </div>
                </div>

                {/* Overview */}
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6 mb-8">
                    <div className="flex items-start gap-4">
                        <FontAwesomeIcon icon={faInfoCircle} className="text-purple-600 text-xl mt-1" />
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-2">¿Qué es el onboarding?</h3>
                            <p className="text-gray-700 mb-3">
                                El onboarding es un proceso guiado que te ayuda a configurar tu negocio en PetitPrime. 
                                Son 5 pasos sencillos que te llevarán desde crear tu marca hasta generar el código QR 
                                que tus clientes escanearán para registrarse.
                            </p>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                                <span className="flex items-center gap-2">
                                    <FontAwesomeIcon icon={faClock} />
                                    Tiempo estimado: 10 minutos
                                </span>
                                <span className="flex items-center gap-2">
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                    5 pasos guiados
                                </span>
                            </div>
                            <p className="text-purple-700 text-sm mt-3">
                                <strong>Nota:</strong> Si es tu primera vez iniciando sesión, serás redirigido automáticamente al proceso de onboarding.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Steps */}
                <div className="space-y-6 mb-8">
                    {onboardingSteps.map((step) => (
                        <div key={step.step} className="bg-white border border-gray-200 rounded-lg p-6">
                            <div className="flex items-start gap-4 mb-4">
                                <div className={`flex-shrink-0 w-14 h-14 ${colorClasses[step.color]?.bg || 'bg-gray-100'} rounded-lg flex items-center justify-center`}>
                                    <FontAwesomeIcon
                                        icon={step.icon}
                                        className={`${colorClasses[step.color]?.text || 'text-gray-600'} text-2xl`}
                                    />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <span className={`text-sm font-bold ${colorClasses[step.color]?.text || 'text-gray-600'} ${colorClasses[step.color]?.bg || 'bg-gray-100'} px-3 py-1 rounded-full`}>
                                            PASO {step.step}
                                        </span>
                                        <h3 className="text-xl font-semibold text-gray-800">{step.title}</h3>
                                    </div>
                                    <p className="text-gray-700 mb-2">{step.description}</p>
                                    <p className="text-gray-600 text-sm mb-3">{step.details}</p>
                                    
                                    {step.tips && (
                                        <div className="bg-gray-50 rounded-lg p-4 mt-4">
                                            <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                                💡 Consejos útiles:
                                            </p>
                                            <ul className="text-sm text-gray-600 space-y-1">
                                                {step.tips.map((tip, index) => (
                                                    <li key={index} className="flex items-start gap-2">
                                                        <span className="text-gray-400">•</span>
                                                        <span>{tip}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    
                                    {step.whatHappens && (
                                        <div className={`${colorClasses[step.color]?.bg || 'bg-blue-50'} rounded-lg p-4 mt-4 border ${colorClasses[step.color]?.border || 'border-blue-200'}`}>
                                            <p className={`text-sm font-semibold ${colorClasses[step.color]?.text || 'text-blue-700'} mb-1 flex items-center gap-2`}>
                                                <FontAwesomeIcon icon={faCheckCircle} />
                                                ¿Qué pasa después?
                                            </p>
                                            <p className={`text-sm ${colorClasses[step.color]?.text || 'text-blue-600'}`}>{step.whatHappens}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Completion */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                    <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        ¡Felicidades! Ya estás listo
                    </h4>
                    <p className="text-green-700 mb-3">
                        Una vez que completes todos los pasos del onboarding, tendrás:
                    </p>
                    <ul className="text-green-700 space-y-2 mb-4">
                        <li className="flex items-start gap-2">
                            <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                            <span>Tu marca configurada y personalizada</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                            <span>Tu tienda agregada con toda su información</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                            <span>Tu código QR generado y listo para imprimir</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                            <span>Tu sistema de fidelización funcionando</span>
                        </li>
                    </ul>
                    <p className="text-green-700">
                        Ahora puedes empezar a compartir tu QR con tus clientes y comenzar a acumular puntos automáticamente.
                    </p>
                </div>

                {/* Can change later */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
                    <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                        <FontAwesomeIcon icon={faInfoCircle} />
                        ¿Puedo cambiar algo después?
                    </h4>
                    <p className="text-blue-700">
                        Sí, puedes modificar toda la información del onboarding en cualquier momento desde tu dashboard. 
                        Puedes editar tu marca, cambiar la información de tu tienda, actualizar tu buyer persona y modificar tus objetivos.
                    </p>
                </div>

                {/* Next Steps */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-6 mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">¿Qué sigue después?</h3>
                    <p className="text-gray-700 mb-4">
                        Ahora que tienes tu cuenta configurada, puedes explorar otras funcionalidades de PetitPrime:
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Link
                            to="/documentation/integraciones/tpv-agora"
                            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                            <div className="flex items-center gap-3 mb-2">
                                <FontAwesomeIcon icon={faQrcode} className="text-blue-600" />
                                <h4 className="font-semibold text-gray-800">Configurar TPV</h4>
                            </div>
                            <p className="text-sm text-gray-600">
                                Conecta tu Terminal de Punto de Venta para que los puntos se acumulen automáticamente
                            </p>
                        </Link>
                        <div className="bg-white border border-gray-200 rounded-lg p-4 opacity-60">
                            <div className="flex items-center gap-3 mb-2">
                                <FontAwesomeIcon icon={faChartBar} className="text-gray-400" />
                                <h4 className="font-semibold text-gray-400">Más guías próximamente</h4>
                            </div>
                            <p className="text-sm text-gray-400">
                                Pronto tendremos más guías sobre campañas, recompensas y estadísticas
                            </p>
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                    <Link
                        to="/documentation/quick-start/login"
                        className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900 font-medium"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Anterior: Iniciar Sesión
                    </Link>
                    <div className="text-sm text-gray-500">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mr-2" />
                        Último paso completado
                    </div>
                </div>
            </div>
        </DocumentationLayout>
    );
};

export default QuickStartOnboarding;

